// Workspace for one Automation Solution Set — the automations a client can
// deploy plus their default timing (enable / start / ramp). Sets are sent in by
// the automation application; here the name, notes and timing can be adjusted.
// Structured like the scenario Automation Impact table.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import {
  resolveAutomationDeployment,
  OMCL_SUB_PHASES,
  groupsForLevels,
  levelGroupById,
} from '@rc/engine';
import type {
  AutomationDeploymentSetting,
  AutomationImpactRow,
  AutomationSolution,
  Client,
  Model,
} from '@rc/engine/types';
import { api } from '../lib/api';
import type { AuthUser } from '../lib/api';
import { toast } from '../components/Toast';
import { canDeleteClientContent } from '../lib/nbaScope';
import { parseGeoclientId, withGeoclientQuery } from '../lib/geoclientContext';

const DC_SEP = '::';

const QUARTER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Q1 Y1' },
  { value: 3, label: 'Q2 Y1' },
  { value: 6, label: 'Q3 Y1' },
  { value: 9, label: 'Q4 Y1' },
  { value: 12, label: 'Q1 Y2' },
  { value: 15, label: 'Q2 Y2' },
  { value: 18, label: 'Q3 Y2' },
  { value: 21, label: 'Q4 Y2' },
  { value: 24, label: 'Q1 Y3' },
  { value: 27, label: 'Q2 Y3' },
  { value: 30, label: 'Q3 Y3' },
  { value: 33, label: 'Q4 Y3' },
];

const RAMP_OPTIONS: { value: number; label: string }[] = [
  { value: 3, label: '3 mo' },
  { value: 6, label: '6 mo' },
  { value: 12, label: '12 mo' },
  { value: 18, label: '18 mo' },
  { value: 24, label: '24 mo' },
];

export default function AutomationSolutionWorkspace({ user }: { user: AuthUser }) {
  const { clientId, solutionId } = useParams<{ clientId?: string; solutionId?: string }>();
  const [searchParams] = useSearchParams();
  const geoclientId = parseGeoclientId(searchParams.get('geoclientId'));
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [solution, setSolution] = useState<AutomationSolution | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isNew = !solutionId || solutionId === 'new';
  const canDelete = clientId ? canDeleteClientContent(user, clientId) : false;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const c = await api.getClient(clientId);
        if (cancelled) return;
        setClient(c);

        if (isNew || !solutionId) return;
        const existing = await api.getAutomationSolution(clientId, solutionId);
        const models = await api.listModels();
        const resolvedModel =
          models.find((m) => m.id === existing.modelId) ||
          models.find((m) => m.id === c.modelId) ||
          models[0];
        if (cancelled) return;
        setModel(resolvedModel || null);
        setSolution(existing);
      } catch (err) {
        if (!cancelled) toast('error', (err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, solutionId, isNew]);

  function patch(updates: Partial<AutomationSolution>) {
    setSolution((prev) => (prev ? { ...prev, ...updates } : prev));
    setDirty(true);
  }

  function patchDeployment(id: string, next: AutomationDeploymentSetting) {
    if (!solution) return;
    patch({
      deployments: {
        ...(solution.deployments || {}),
        [id]: next,
      },
    });
  }

  async function handleSave() {
    if (!clientId || !solution) return;
    if (!solution.name.trim()) {
      toast('error', 'Automation solution set requires a name.');
      return;
    }
    setSaving(true);
    try {
      const saved = await api.saveAutomationSolution(clientId, solution, geoclientId);
      toast('success', 'Automation solution set saved.');
      setSolution(saved);
      setDirty(false);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!clientId || !solution?.id) return;
    if (!window.confirm(`Delete "${solution.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteAutomationSolution(clientId, solution.id, geoclientId);
      toast('success', 'Automation solution set deleted.');
      navigate(withGeoclientQuery(`/clients/${clientId}`, geoclientId), { replace: true });
    } catch (err) {
      toast('error', (err as Error).message);
    }
  }

  const automations = useMemo(
    () => (solution?.automations || []).filter(Boolean),
    [solution],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  if (isNew) {
    return (
      <div className="px-6 py-8 w-full max-w-3xl mx-auto">
        <div className="card card-body text-sm text-slate-600 space-y-2">
          <p className="font-medium text-slate-800">Automation solution sets are sent in</p>
          <p>
            Sets arrive from the automation application rather than being built here. Once a
            set has been received it appears on the client page and in the scenario pickers.
          </p>
          <Link to={withGeoclientQuery(`/clients/${clientId}`, geoclientId)} className="btn-secondary w-fit">
            <ArrowLeft size={14} /> Back to client
          </Link>
        </div>
      </div>
    );
  }

  if (!solution || !model) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        Could not load automation solution set.
      </div>
    );
  }

  const enabledCount = automations.filter((a) => {
    const s = solution.deployments?.[a.id] || resolveAutomationDeployment(a);
    return s.enabled !== false;
  }).length;

  const savedBy = solution.updatedBy?.displayName || solution.createdBy?.displayName;
  const savedAt = solution.updatedAt || solution.createdAt;

  return (
    <div className="px-6 py-8 w-full max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to={withGeoclientQuery(`/clients/${clientId}`, geoclientId)}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 text-slate-500"
          >
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">
              {client?.name || clientId} / Automation solution sets
            </div>
            <h1 className="text-xl font-semibold text-slate-900">
              {solution.name || 'Untitled set'}
            </h1>
            {savedBy && savedAt && (
              <p className="text-xs text-slate-400 mt-0.5">
                Last saved by <span className="font-medium text-slate-600">{savedBy}</span>
                {' · '}
                {new Date(savedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="btn-secondary text-rose-600 hover:border-rose-300 hover:bg-rose-50"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary">
            <Save size={14} />
            {saving ? 'Saving…' : dirty ? 'Save*' : 'Save'}
          </button>
        </div>
      </div>

      <div className="card card-body space-y-4">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            type="text"
            placeholder="e.g. FY26 core automation rollout"
            value={solution.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="label">
            Notes <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <textarea
            className="input resize-none"
            rows={2}
            placeholder="Source system, intended use, or rollout notes…"
            value={solution.notes || ''}
            onChange={(e) => patch({ notes: e.target.value })}
          />
        </div>
        <p className="text-xs text-slate-500">
          Model: <span className="font-medium text-slate-700">{model.name}</span>
          {' · '}
          {enabledCount} of {automations.length} automations enabled
          {solution.sample && (
            <>
              {' · '}
              <span className="text-amber-700">Sample data until the automation feed is live</span>
            </>
          )}
          {solution.source?.system && (
            <>
              {' · '}
              Sent by <span className="font-medium text-slate-700">{solution.source.system}</span>
              {solution.source.receivedAt &&
                ` on ${new Date(solution.source.receivedAt).toLocaleDateString()}`}
            </>
          )}
        </p>
      </div>

      <div className="card overflow-hidden">
        <div className="card-header">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Sparkles size={14} className="text-brand-purple-500" />
              Deployment plan
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Default enable / start / ramp for each automation in this set. Scenarios copy
              these when the set is applied and can fine-tune them on Automation Impact.
            </p>
          </div>
        </div>
        {automations.length === 0 ? (
          <div className="card-body text-sm text-slate-500">
            This set has no automations.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1100px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/60">
                  <th className="text-left py-2 pl-4 pr-3 font-medium w-10">On</th>
                  <th className="text-left py-2 pr-3 font-medium min-w-[200px]">Automation</th>
                  <th className="text-left py-2 px-2 font-medium min-w-[180px] text-sky-700">
                    Discipline
                  </th>
                  <th className="text-left py-2 px-2 font-medium min-w-[160px] text-amber-800">
                    Roles
                  </th>
                  <th className="text-left py-2 px-2 font-medium min-w-[160px] text-indigo-700">
                    Tasks
                  </th>
                  <th className="text-right py-2 px-2 font-medium whitespace-nowrap">% saved</th>
                  <th className="text-left py-2 px-2 font-medium">Start</th>
                  <th className="text-left py-2 pr-4 pl-2 font-medium">Ramp</th>
                </tr>
              </thead>
              <tbody>
                {automations.map((a) => {
                  const setting =
                    solution.deployments?.[a.id] || resolveAutomationDeployment(a);
                  const enabled = setting.enabled !== false;
                  const attribution = resolveAutomationAttribution(a, model);
                  return (
                    <tr
                      key={a.id}
                      className={clsx(
                        'border-b border-slate-100',
                        !enabled && 'opacity-50',
                      )}
                    >
                      <td className="py-2.5 pl-4 pr-3 align-top">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={enabled}
                          onChange={(e) =>
                            patchDeployment(a.id, {
                              ...setting,
                              enabled: e.target.checked,
                            })
                          }
                        />
                      </td>
                      <td className="py-2.5 pr-3 align-top">
                        <div className="font-medium text-slate-800">{a.label}</div>
                        {a.description && (
                          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                            {a.description}
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-2 align-top">
                        <ChipRow
                          emptyLabel="All disciplines & channels"
                          tone="sky"
                          items={attribution.scopes}
                        />
                      </td>
                      <td className="py-2.5 px-2 align-top">
                        <ChipRow
                          emptyLabel="All roles"
                          tone="amber"
                          items={attribution.roles}
                        />
                      </td>
                      <td className="py-2.5 px-2 align-top">
                        <ChipRow
                          emptyLabel="All task areas"
                          tone="indigo"
                          items={attribution.tasks}
                        />
                      </td>
                      <td className="py-2.5 px-2 align-top text-right tabular-nums text-slate-700">
                        {Math.round((Number(a.reductionPct) || 0) * 100)}%
                      </td>
                      <td className="py-2.5 px-2 align-top">
                        <select
                          className="input py-1 px-2 text-xs w-auto"
                          value={Number(setting.startMonth) || 0}
                          disabled={!enabled}
                          onChange={(e) =>
                            patchDeployment(a.id, {
                              ...setting,
                              startMonth: Number(e.target.value),
                            })
                          }
                        >
                          {QUARTER_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2.5 pr-4 pl-2 align-top">
                        <select
                          className="input py-1 px-2 text-xs w-auto"
                          value={Number(setting.rampMonths) || 12}
                          disabled={!enabled}
                          onChange={(e) =>
                            patchDeployment(a.id, {
                              ...setting,
                              rampMonths: Number(e.target.value),
                            })
                          }
                        >
                          {RAMP_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ChipRow({
  items,
  emptyLabel,
  tone,
}: {
  items: string[];
  emptyLabel: string;
  tone: 'sky' | 'amber' | 'indigo';
}) {
  const toneClass =
    tone === 'sky'
      ? 'bg-sky-50 text-sky-800 ring-sky-200'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-900 ring-amber-200'
        : 'bg-indigo-50 text-indigo-800 ring-indigo-200';

  if (items.length === 0) {
    return (
      <div className="flex flex-wrap gap-1">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 italic">
          {emptyLabel}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((label) => (
        <span
          key={label}
          className={clsx('text-[10px] px-1.5 py-0.5 rounded ring-1', toneClass)}
          title={label}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function resolveAutomationAttribution(
  automation: AutomationImpactRow,
  model: Model,
): { scopes: string[]; roles: string[]; tasks: string[] } {
  const disciplineLabel = (id: string) =>
    model.disciplines?.find((d) => d.id === id)?.label || id;
  const channelLabel = (id: string) =>
    id === 'all' ? null : model.channels?.find((c) => c.id === id)?.label || id;

  const scopes = (automation.disciplineChannelKeys || []).map((k) => {
    const [d, c] = k.split(DC_SEP);
    const dl = disciplineLabel(d || k);
    const cl = c ? channelLabel(c) : null;
    return cl ? `${dl} · ${cl}` : dl;
  });

  const roles = groupsForLevels(automation.levelIds || []).map((g) => levelGroupById(g).label);

  const taskLabel = (id: string) =>
    OMCL_SUB_PHASES.find((s) => s.id === id)?.label || id;
  const tasks = (automation.subPhaseIds || []).map(taskLabel);

  return { scopes, roles, tasks };
}
